import * as React from "react";

import { PropTypes } from "prop-types";
import cs from "classnames";

import { Carousel } from "@lululemon/ecom-pattern-library";
import DarIcon from "../../widgets/icon";
import IconPrev from "../../../assets/img/svg/nav-prev.svg";
import IconNext from "../../../assets/img/svg/nav-next.svg";

import { getBreakpoint } from "../../../lib/breakpoints";

const CarouselPrevIcon = () => (
  <DarIcon
    content={IconPrev}
    title="Prev Carousel"
    className="prev-next-icon"
  />
);

const CarouselNextIcon = () => (
  <DarIcon
    content={IconNext}
    title="Next Carousel"
    className="prev-next-icon"
  />
);

const CarouselWrapper = ({
  showArrows,
  children,
  onPrev,
  onNext,
  isGrayNavPrev,
  isGrayNavNext,
}) => (
  <>
    {showArrows && (
      <button
        data-testid="prev-button"
        aria-hidden="true"
        className={cs("nav-prev", { "grayed-nav-icon": isGrayNavPrev })}
        type="button"
        tabIndex="-1"
        onClick={onPrev}
      >
        <CarouselPrevIcon />
      </button>
    )}
    {children}
    {showArrows && (
      <button
        data-testid="next-button"
        aria-hidden="true"
        className={cs("nav-next", { "grayed-nav-icon": isGrayNavNext })}
        type="button"
        tabIndex="-1"
        onClick={onNext}
      >
        <CarouselNextIcon />
      </button>
    )}
  </>
);

export const DesktopSwatchCarousel = ({ activeSwatch, inView, children }) => {
  const swatchLength = React.Children.toArray(children)?.length;
  const [slideIndex, setSlideIndex] = React.useState(0);
  const [showArrows, setShowArrows] = React.useState(true);

  const [{ isGrayNavPrev, isGrayNavNext }, setGrayedState] = React.useState({
    isGrayNavPrev: true,
    isGrayNavNext: false,
  });

  const breakPoint = getBreakpoint();

  const visibleSwatches = React.useMemo(() => {
    const defaultConfig = { desktop: 5, large: 7, xlarge: 7 };
    return defaultConfig[breakPoint] || 5;
  }, [breakPoint]);

  React.useEffect(() => {
    setShowArrows(swatchLength > visibleSwatches);
    setSlideIndex(activeSwatch);
  }, [swatchLength, visibleSwatches]);

  if (!swatchLength) return null;

  const onSlideChanged = (value) => {
    const lastIndex = swatchLength - 1;
    setSlideIndex(value);
    setGrayedState({
      isGrayNavPrev: value === 0,
      isGrayNavNext: value === lastIndex,
    });
  };

  const onPrev = () => {
    const newIndex = slideIndex - visibleSwatches;
    onSlideChanged(newIndex > 0 ? newIndex : 0);
  };

  const onNext = () => {
    const lastIndex = swatchLength - 1;
    const newIndex = slideIndex + visibleSwatches;
    onSlideChanged(newIndex < lastIndex ? newIndex : lastIndex);
  };

  return (
    <CarouselWrapper
      {...{
        showArrows,
        isGrayNavPrev,
        isGrayNavNext,
        onPrev,
        onNext,
      }}
    >
      {(inView || process.env.NODE_ENV === "test") && (
        <div className="swatch-carousel__element">
          <Carousel
            slideIndex={slideIndex}
            onSlideIndexChange={onSlideChanged}
            slidesPerPage={visibleSwatches}
            slidesPerScroll={visibleSwatches}
          >
            {children}
          </Carousel>
        </div>
      )}
    </CarouselWrapper>
  );
};

CarouselWrapper.defaultProps = {
  showArrows: true,
  isGrayNavPrev: true,
  isGrayNavNext: false,
};

CarouselWrapper.propTypes = {
  showArrows: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  isGrayNavPrev: PropTypes.bool,
  isGrayNavNext: PropTypes.bool,
};

DesktopSwatchCarousel.defaultProps = {
  activeSwatch: 0,
  inView: false,
};

DesktopSwatchCarousel.propTypes = {
  activeSwatch: PropTypes.number,
  inView: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

export default React.memo(DesktopSwatchCarousel);
